import { useEffect, useMemo, useState } from 'react';

import { faMagnifyingGlass } from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import {
  faCheckCircle,
  faChevronLeft,
  faTimesCircle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/videa-components/src/index';
import { useAtomValue, useSetAtom } from 'jotai';
import { useHistory } from 'react-router-dom';

import { PatientNamePrivacy } from '../../common-components/composed/PatientNamePrivacy';
import { PrintButton } from '../../layout/header/topbar/PrintButton';
import { toBusinessName } from '../../utils/string';
import { PrivacyButton } from '../patientList/PrivacyButton';
import { useShouldHideHuddle } from '../patientList/useShouldHideHuddle';

import { selectedPatientZuubDataAtom, selectedPlanAtom } from './atoms';
import {
  getPlanStatusByPayerName,
  getPlanTypesForPayer,
  getSelectedPlan,
  getUniquePayerNames,
  isPlanActive,
} from './helpers';

interface BenefitsPageTopbarProps {}

export const BenefitsPageTopbar = ({}: BenefitsPageTopbarProps) => {
  const history = useHistory();
  const { t } = useLingui();
  const hideHuddle = useShouldHideHuddle();
  // Get the stored Zuub data for the current patient from the atom
  const currentPatientData = useAtomValue(selectedPatientZuubDataAtom);
  const defaultPlan = currentPatientData?.detailedData?.[0];
  const setSelectedPlan = useSetAtom(selectedPlanAtom);

  // State for selected plan name and plan type
  const [selectedPayerName, setSelectedPayerName] = useState<string>(
    defaultPlan?.plan?.payerName || '',
  );
  const [selectedPlanType, setSelectedPlanType] = useState<string>(
    defaultPlan?.plan?.planType || '',
  );

  // Get the currently selected plan based on dropdown selections
  const currentPlan = getSelectedPlan(
    currentPatientData,
    selectedPayerName,
    selectedPlanType,
  );

  // Derive patient name from Zuub data
  const patientName = currentPatientData?.patientName || 'Unknown Patient';

  // Get unique payer names and plan types from available data
  const availablePayerNames = useMemo(
    () => getUniquePayerNames(currentPatientData),
    [currentPatientData],
  );

  // Get plan types available for the currently selected payer
  const availablePlanTypes = useMemo(
    () => getPlanTypesForPayer(currentPatientData, selectedPayerName),
    [currentPatientData, selectedPayerName],
  );

  // Update the selected plan atom when selections change
  useEffect(() => {
    setSelectedPlan(currentPlan);
  }, [currentPlan, setSelectedPlan]);

  // Reset plan type if it's not available for the selected payer
  useEffect(() => {
    if (selectedPayerName && availablePlanTypes.length > 0) {
      if (!availablePlanTypes.includes(selectedPlanType)) {
        // Reset to the first available plan type for the selected payer
        setSelectedPlanType(availablePlanTypes[0]);
      }
    }
  }, [selectedPayerName, availablePlanTypes, selectedPlanType]);

  const handleBackToPatientList = () => {
    history.push('/auto-verify');
  };

  return (
    <div className="vai-bg-white vai-px-4 vai-py-6 vai-shadow-sm vai-z-10 vai-h-[124px]">
      {/* First Row - Back to Patient List and Action Buttons */}
      <div className="vai-flex vai-items-center vai-justify-between vai-h-8 vai-mb-4">
        <button
          onClick={handleBackToPatientList}
          className="vai-flex vai-items-center vai-gap-2 vai-text-gray-700 hover:vai-text-gray-900 vai-transition-colors vai-duration-200"
        >
          <FontAwesomeIcon icon={faChevronLeft} className="vai-text-sm" />
          <span className="vai-text-sm vai-font-medium">
            {hideHuddle ? (
              <Trans>All Patients</Trans>
            ) : (
              <Trans>Dashboard</Trans>
            )}
          </span>
        </button>

        {/* Action Buttons - Top Right */}
        <div className="vai-flex vai-items-center vai-gap-2">
          {/* Print Button */}
          {/* <PrintButton appointmentIdsToTasks={{}} appointments={[]} /> */}
        </div>
      </div>

      {/* Second Row - Patient Name, Selects, and Actions */}
      <div className="vai-flex vai-items-center vai-justify-between vai-h-10">
        {/* Left side - Patient Name and Selects */}
        <div className="vai-flex vai-items-center vai-gap-4">
          {/* Patient Name - aligned with Back to Patient List */}
          <h1 className="vai-text-xl vai-font-bold vai-text-gray-900 vai-whitespace-nowrap vai-ml-4">
            <PatientNamePrivacy name={patientName} noWrap={true} />
          </h1>

          {/* Vertical Divider */}
          <div className="vai-h-10 vai-w-px vai-bg-gray-300 vai-mx-2"></div>

          {/* Insurance Provider Select */}
          <Select
            value={toBusinessName(selectedPayerName)}
            onValueChange={(value) => {
              // Find the original payer name from the formatted value
              const originalPayerName = availablePayerNames.find(
                (name) => toBusinessName(name) === value,
              );
              if (originalPayerName) {
                setSelectedPayerName(originalPayerName);
              }
            }}
          >
            <SelectTrigger className="vai-w-auto vai-whitespace-nowrap">
              <SelectValue placeholder={t`Select insurance provider`} />
            </SelectTrigger>
            <SelectContent>
              {availablePayerNames.map((payerName) => {
                const formattedName = toBusinessName(payerName);
                const isCurrentPayer = payerName === selectedPayerName;

                // Get plan status using helper function
                const planStatus = getPlanStatusByPayerName(
                  currentPatientData,
                  payerName,
                );
                const isActive = isPlanActive(planStatus);

                return (
                  <SelectItem
                    key={payerName}
                    value={formattedName}
                    disabled={!isActive}
                  >
                    <div className="vai-flex vai-items-center vai-gap-2">
                      {formattedName}
                      {isCurrentPayer && (
                        <FontAwesomeIcon
                          icon={isActive ? faCheckCircle : faTimesCircle}
                          className={`vai-h-3 vai-w-3 vai-mr-1 ${
                            isActive ? 'vai-text-green-700' : 'vai-text-red-500'
                          }`}
                        />
                      )}
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>

          {/* Plan Type Select - only show if user has plan types under currently selected payer */}
          {availablePlanTypes.length > 0 && (
            <Select
              value={selectedPlanType}
              onValueChange={setSelectedPlanType}
            >
              <SelectTrigger className="vai-w-auto">
                <SelectValue placeholder={t`Plan type`} />
              </SelectTrigger>
              <SelectContent>
                {availablePlanTypes.map((planType) => {
                  return (
                    <SelectItem key={planType} value={planType}>
                      {planType.toUpperCase()}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Right side - Privacy Button */}
        <div className="vai-flex vai-items-center">
          <PrivacyButton source="AUTOVERIFY" />
        </div>

        {/* Right side - Search Input */}
        {/* <div className="vai-relative vai-w-80">
          <FontAwesomeIcon
            icon={faMagnifyingGlass}
            className="vai-absolute vai-left-3 vai-top-1/2 vai-transform vai--translate-y-1/2 vai-text-gray-400 vai-text-sm vai-pointer-events-none vai-z-10"
          />
          <Input
            placeholder="Search ADA code or service"
            className="vai-pl-8"
          />
        </div> */}
      </div>
    </div>
  );
};
